import { useEffect, useMemo, useRef } from 'react'

import { captureException } from '@sentry/nextjs'

import AnimatedLoadingPage from '@components/AnimatedLoadingPage'
import ErrorBoundary from '@components/ErrorBoundary'

import useAppQueryState from '@hooks/useAppQueryState'
import { useAutoLoanSearch } from '@hooks/useAutoLoanSearch'
import useInscalarTrackEvent from '@hooks/useInscalarTrackEvent'

import { useAlternativeProductCategoriesQuery } from '@queries/useAlternativeProductCategoriesQuery'
import { useHasEverUsedNativeAppQuery } from '@queries/useHasEverUsedNativeAppQuery'
import { useMinimumGuaranteedCreditLimitQuery } from '@queries/useMinimumGuaranteedCreditLimitQuery'
import { useScoredCategoriesQuery } from '@queries/useScoredCategoriesQuery'

import getCategoryByCategoryId from '@utils/getCategoryByCategoryId'

import ScoredTable from './components/ScoredTable/ScoredTable'
import getHighlightCard from './components/ScoredTable/utils/getHighlightCard'
import ScoredTableError from './components/ScoredTableError'
import ScoredTableSkeleton from './components/ScoredTableSkeleton/ScoredTableSkeleton'
import getRedirectToAlternativeProducts from './utils/getRedirectToAlternativeProducts'

const ScoredTableRoute = () => {
    const { appQueryState, goToRouteWithAppState } = useAppQueryState()
    const { trackDecline, trackSubmit } = useInscalarTrackEvent()
    const hasSubmitRun = useRef(false)

    const scoredCategoriesResponse = useScoredCategoriesQuery({
        body: {
            scoreId: appQueryState.scoreId!,
            customerId: appQueryState.customerId!,
            category: appQueryState.categoryId!,
        },
        select: data => data,
    })

    const shouldFetchAlternativeProducts = getRedirectToAlternativeProducts(
        scoredCategoriesResponse
    )

    const {
        getLoansRedirectUrl,
        hasAvailableLoans,
        isLoading: isLoadingAutoLoanSearch,
    } = useAutoLoanSearch({
        shouldFetchAlternativeProducts,
    })

    const minCreditLimitResponse = useMinimumGuaranteedCreditLimitQuery({
        customerId: appQueryState.customerId,
    })

    const hasUsedNativeAppResponse = useHasEverUsedNativeAppQuery({
        customerId: appQueryState.customerId!,
        shouldFetch: appQueryState.showAppPromotion === 'true',
    })

    const shouldFetchCreditCardAlternativeProducts =
        shouldFetchAlternativeProducts &&
        !isLoadingAutoLoanSearch &&
        !hasAvailableLoans

    const altProductsResponse = useAlternativeProductCategoriesQuery({
        body: {
            scoreId: appQueryState.scoreId!,
            customerId: appQueryState.customerId!,
        },
        shouldFetchAlternativeProducts:
            shouldFetchCreditCardAlternativeProducts,
    })

    const highlightCard = getHighlightCard({
        category: 'BestCards',
        highlightCardPath: appQueryState.isCardSpecific
            ? appQueryState.scoredCardUrl
            : undefined,
        scoredCategories: scoredCategoriesResponse.data?.scoredCategories,
        allActiveCards: scoredCategoriesResponse.data?.allActiveCards,
    })

    useEffect(() => {
        if (hasSubmitRun.current) return

        hasSubmitRun.current = true

        trackSubmit({
            step: 'credit_cards_search',
            journey: 'credit_cards',
            journeyCategory: 'credit_cards',
        })
    }, [trackSubmit])

    useEffect(() => {
        if (hasAvailableLoans) {
            const loansUrl = getLoansRedirectUrl()
            if (loansUrl) {
                trackDecline({
                    step: 'credit_cards_scoring',
                    journey: 'credit_cards',
                    journeyCategory: 'credit_cards',
                })

                window.location.href = loansUrl
            }
        }
    }, [hasAvailableLoans, getLoansRedirectUrl])

    useEffect(() => {
        if (altProductsResponse.isSuccess) {
            goToRouteWithAppState('alternative-products', {
                queryParameters: { journey: 'autoLoanSearch' },
            })
        }
    }, [altProductsResponse.isSuccess, goToRouteWithAppState])

    const activeCategoryId = useMemo(() => {
        if (
            scoredCategoriesResponse.isSuccess &&
            getCategoryByCategoryId(
                scoredCategoriesResponse.data.scoredCategories,
                appQueryState.categoryId!
            ) == null
        ) {
            return 'BestCards'
        }
        return appQueryState.categoryId!
    }, [scoredCategoriesResponse.isSuccess])

    switch (true) {
        case scoredCategoriesResponse.isLoading: {
            return (
                <>
                    <AnimatedLoadingPage fill={false} />
                    <ScoredTableSkeleton />
                </>
            )
        }
        case isLoadingAutoLoanSearch ||
            hasAvailableLoans ||
            (shouldFetchCreditCardAlternativeProducts &&
                altProductsResponse.isLoading) ||
            altProductsResponse.isSuccess: {
            return (
                <>
                    <AnimatedLoadingPage
                        fill={false}
                        heading="Finding you other options"
                        supportingText="Checking your eligibility"
                    />
                    <ScoredTableSkeleton />
                </>
            )
        }
        case scoredCategoriesResponse.isSuccess &&
            scoredCategoriesResponse.data != null: {
            return (
                <ErrorBoundary
                    fallback={<ScoredTableError />}
                    onError={({ error }) => captureException(error)}
                >
                    <ScoredTable
                        highlightCard={highlightCard}
                        activeCategoryId={activeCategoryId}
                        scoredCategories={
                            scoredCategoriesResponse.data?.scoredCategories!
                        }
                        scoredExperiments={
                            scoredCategoriesResponse.data?.experiments
                        }
                        thirdPartyReference={
                            scoredCategoriesResponse.data?.thirdPartyReference
                        }
                        hasEverUsedNativeApp={
                            hasUsedNativeAppResponse.data?.hasEverUsedNativeApp
                        }
                        minCreditLimitData={minCreditLimitResponse?.data}
                    />
                </ErrorBoundary>
            )
        }
        case scoredCategoriesResponse.isError:
        default: {
            return <ScoredTableError error={scoredCategoriesResponse.error} />
        }
    }
}

export default ScoredTableRoute
